import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderPlus,
  Edit,
  Trash2,
  Plus,
  X,
  Search,
  BookOpen,
  FolderTree,
  Users,
  Check,
  X as XIcon,
  Save,
  UserPlus
} from 'lucide-react';
import { apiService } from '../services/api';
import { LearningCollection, Tutorial, Category } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';

interface CollectionManagerProps {
  onCollectionSelect?: (collection: LearningCollection) => void;
}

const CollectionManager: React.FC<CollectionManagerProps> = ({ onCollectionSelect }) => {
  const { user } = useAuth();
  const [collections, setCollections] = useState<LearningCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<LearningCollection | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<LearningCollection | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '', icon: '' });
  const [availableTutorials, setAvailableTutorials] = useState<Tutorial[]>([]);
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [selectedTutorials, setSelectedTutorials] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [contentSearchQuery, setContentSearchQuery] = useState('');
  const [tutorialsLoading, setTutorialsLoading] = useState(false);
  const [tutorialsPage, setTutorialsPage] = useState(1);
  const [tutorialsHasMore, setTutorialsHasMore] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const canManage = user?.roles?.includes('admin') || 
                   user?.roles?.includes('manager') || 
                   user?.roles?.includes('instructor');
  const canAssign = user?.roles?.includes('admin') || user?.roles?.includes('manager');

  useEffect(() => {
    loadCollections();
  }, []);

  // Auto-open modal when collections are loaded and we have a collection ID
  useEffect(() => {
    const collectionIdToOpen = sessionStorage.getItem('openCollectionModal');
    if (collectionIdToOpen && collections.length > 0 && !showAddContentModal) {
      const collection = collections.find(c => c.id === collectionIdToOpen);
      if (collection) {
        sessionStorage.removeItem('openCollectionModal');
        openAddContentModal(collection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections, showAddContentModal]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLearningCollections();
      if (response.success && response.data) {
        setCollections(response.data);
      }
    } catch (error: any) {
      console.error('Error loading collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Collection name is required');
      return;
    }

    try {
      const response = await apiService.createLearningCollection(formData);
      if (response.success) {
        toast.success('Collection created successfully');
        setShowCreateModal(false);
        setFormData({ name: '', description: '', icon: '' });
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to create collection');
      }
    } catch (error: any) {
      console.error('Error creating collection:', error);
      toast.error('Failed to create collection');
    }
  };

  const handleEdit = async () => {
    if (!editingCollection || !formData.name.trim()) {
      toast.error('Collection name is required');
      return;
    }

    try {
      const response = await apiService.updateLearningCollection(editingCollection.id, formData);
      if (response.success) {
        toast.success('Collection updated successfully');
        setShowEditModal(false);
        setEditingCollection(null);
        setFormData({ name: '', description: '', icon: '' });
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to update collection');
      }
    } catch (error: any) {
      console.error('Error updating collection:', error);
      toast.error('Failed to update collection');
    }
  };

  const handleDelete = async (collection: LearningCollection) => {
    if (!confirm(`Are you sure you want to delete "${collection.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteLearningCollection(collection.id);
      if (response.success) {
        toast.success('Collection deleted successfully');
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to delete collection');
      }
    } catch (error: any) {
      console.error('Error deleting collection:', error);
      toast.error('Failed to delete collection');
    }
  };

  const openEditModal = (collection: LearningCollection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name,
      description: collection.description || '',
      icon: collection.icon || ''
    });
    setShowEditModal(true);
  };

  const openAddContentModal = async (collection: LearningCollection) => {
    setSelectedTutorials([]);
    setSelectedCategories([]);
    setContentSearchQuery('');
    setTutorialsPage(1);
    setTutorialsHasMore(true);
    setAvailableTutorials([]);
    setAvailableCategories([]);
    
    try {
      // Fetch full collection details with tutorials and categories
      const fullCollectionResponse = await apiService.getLearningCollection(collection.id);
      if (fullCollectionResponse.success && fullCollectionResponse.data) {
        const fullCollection = fullCollectionResponse.data;
        console.log('Loaded collection details:', {
          id: fullCollection.id,
          tutorialsCount: fullCollection.tutorials?.length || 0,
          categoriesCount: fullCollection.categories?.length || 0,
          tutorials: fullCollection.tutorials,
          categories: fullCollection.categories
        });
        setSelectedCollection(fullCollection);
      } else {
        console.warn('Failed to load full collection, using basic data:', collection);
        // Fallback to the collection we have if API call fails
        setSelectedCollection(collection);
      }
    } catch (error) {
      console.error('Error loading collection details:', error);
      // Fallback to the collection we have if API call fails
      setSelectedCollection(collection);
    }
    
    // Load initial batch of tutorials and categories
    await loadTutorialsPage(1, '');
    await loadCategories();
    
    setShowAddContentModal(true);
  };

  const loadTutorialsPage = async (page: number, searchQuery: string = '') => {
    if (tutorialsLoading) return;
    
    try {
      setTutorialsLoading(true);
      const limit = 50; // Smaller batches for better performance
      const tutorialsResponse = await apiService.getTutorials(page, limit);
      
      if (tutorialsResponse && tutorialsResponse.tutorials) {
        const newTutorials = tutorialsResponse.tutorials || [];
        
        if (page === 1) {
          setAvailableTutorials(newTutorials);
        } else {
          setAvailableTutorials(prev => [...prev, ...newTutorials]);
        }
        
        // Check if there are more pages
        if (tutorialsResponse.pagination) {
          setTutorialsHasMore(page < tutorialsResponse.pagination.totalPages);
          setTutorialsPage(page);
        } else {
          setTutorialsHasMore(newTutorials.length === limit);
          setTutorialsPage(page);
        }
      }
    } catch (error) {
      console.error('Error loading tutorials:', error);
      toast.error('Failed to load tutorials');
    } finally {
      setTutorialsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const categoriesResponse = await apiService.getCategories();
      
      if (categoriesResponse.success && categoriesResponse.data) {
        const categories = categoriesResponse.data || [];
        // Build tree structure for nested categories
        const categoryTree = buildCategoryTree(categories);
        setAvailableCategories(categoryTree);
      } else {
        setAvailableCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
      setAvailableCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const buildCategoryTree = (categories: Category[]): Category[] => {
    const categoryMap = new Map<string, Category & { subcategories?: Category[] }>();
    const rootCategories: Category[] = [];

    // First pass: create map of all categories
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, subcategories: [] });
    });

    // Second pass: build tree structure
    categories.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parentCategoryId && categoryMap.has(cat.parentCategoryId)) {
        const parent = categoryMap.get(cat.parentCategoryId)!;
        if (!parent.subcategories) {
          parent.subcategories = [];
        }
        parent.subcategories.push(category);
      } else {
        rootCategories.push(category);
      }
    });

    return rootCategories;
  };

  const handleAddContent = async () => {
    if (!selectedCollection) return;

    try {
      const promises: Promise<any>[] = [];
      
      if (selectedTutorials.length > 0) {
        promises.push(apiService.addTutorialsToCollection(selectedCollection.id, selectedTutorials));
      }
      
      if (selectedCategories.length > 0) {
        promises.push(apiService.addCategoriesToCollection(selectedCollection.id, selectedCategories));
      }

      if (promises.length === 0) {
        toast.error('Please select at least one tutorial or category');
        return;
      }

      await Promise.all(promises);
      toast.success('Content added to collection');
      setShowAddContentModal(false);
      setSelectedCollection(null);
      loadCollections();
    } catch (error: any) {
      console.error('Error adding content:', error);
      toast.error('Failed to add content to collection');
    }
  };

  const handleRemoveTutorial = async (collectionId: string, tutorialId: string) => {
    if (!tutorialId || tutorialId === 'undefined') {
      console.error('Invalid tutorial ID:', tutorialId);
      toast.error('Invalid tutorial ID');
      return;
    }

    try {
      const response = await apiService.removeTutorialFromCollection(collectionId, tutorialId);
      if (response.success) {
        toast.success('Tutorial removed from collection');
        // Reload the full collection to update the modal
        if (selectedCollection) {
          const fullCollectionResponse = await apiService.getLearningCollection(collectionId);
          if (fullCollectionResponse.success && fullCollectionResponse.data) {
            setSelectedCollection(fullCollectionResponse.data);
          }
        }
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to remove tutorial');
      }
    } catch (error: any) {
      console.error('Error removing tutorial:', error);
      toast.error(error?.message || 'Failed to remove tutorial');
    }
  };

  const handleRemoveCategory = async (collectionId: string, categoryId: string) => {
    if (!categoryId || categoryId === 'undefined') {
      console.error('Invalid category ID:', categoryId);
      toast.error('Invalid category ID');
      return;
    }

    try {
      const response = await apiService.removeCategoryFromCollection(collectionId, categoryId);
      if (response.success) {
        toast.success('Category removed from collection');
        // Reload the full collection to update the modal
        if (selectedCollection) {
          const fullCollectionResponse = await apiService.getLearningCollection(collectionId);
          if (fullCollectionResponse.success && fullCollectionResponse.data) {
            setSelectedCollection(fullCollectionResponse.data);
          }
        }
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to remove category');
      }
    } catch (error: any) {
      console.error('Error removing category:', error);
      toast.error(error?.message || 'Failed to remove category');
    }
  };

  const openAssignModal = async (collection: LearningCollection) => {
    setSelectedCollection(collection);
    setSelectedUsers([]);
    setUserSearchQuery('');
    
    try {
      const response = await apiService.getUsers({ limit: 1000 });
      if (response.success && response.data && response.data.users) {
        setAllUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
    
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    if (!selectedCollection || selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      const response = await apiService.assignCollectionToUsers(selectedCollection.id, selectedUsers);
      if (response.success) {
        toast.success(`Collection assigned to ${response.data?.assigned || 0} user(s)`);
        setShowAssignModal(false);
        setSelectedCollection(null);
        loadCollections();
      } else {
        toast.error(response.error || 'Failed to assign collection');
      }
    } catch (error: any) {
      console.error('Error assigning collection:', error);
      toast.error('Failed to assign collection');
    }
  };

  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    collection.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTutorials = availableTutorials.filter(tutorial => {
    if (!contentSearchQuery.trim()) return true; // Show all when search is empty
    
    const query = contentSearchQuery.toLowerCase().trim();
    const title = tutorial.title?.toLowerCase() || '';
    const description = tutorial.description?.toLowerCase() || '';
    const category = tutorial.category?.toLowerCase() || '';
    const product = tutorial.product?.toLowerCase() || '';
    
    return title.includes(query) || 
           description.includes(query) || 
           category.includes(query) ||
           product.includes(query);
  });

  const filteredCategories = availableCategories.filter(category => {
    if (!contentSearchQuery.trim()) return true; // Show all when search is empty
    
    const query = contentSearchQuery.toLowerCase().trim();
    const name = category.name?.toLowerCase() || '';
    const description = category.description?.toLowerCase() || '';
    
    return name.includes(query) || description.includes(query);
  });

  const filteredUsers = allUsers.filter(user =>
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  if (!canManage) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have permission to manage collections.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading collections...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Learning Collections</h2>
          <p className="text-sm text-gray-500 mt-1">Manage collections for user assignments and completion tracking</p>
        </div>
        <button
          onClick={() => {
            setFormData({ name: '', description: '', icon: '' });
            setShowCreateModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Collection</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Collections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCollections.map((collection) => (
          <motion.div
            key={collection.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3 flex-1">
                {collection.icon && (
                  <div className="text-2xl">{collection.icon}</div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{collection.name}</h3>
                  {collection.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">{collection.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => openEditModal(collection)}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(collection)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-1">
                  <BookOpen className="w-3 h-3" />
                  <span>{collection.totalTutorialCount || 0} tutorials</span>
                </span>
                <span className="flex items-center space-x-1">
                  <FolderTree className="w-3 h-3" />
                  <span>{collection.categoryCount || 0} categories</span>
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openAddContentModal(collection)}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-1"
                  title="Manage tutorials and categories in this collection"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manage Content</span>
                </button>
                {canAssign && (
                  <button
                    onClick={() => openAssignModal(collection)}
                    className="px-3 py-2 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors flex items-center justify-center"
                    title="Assign this collection to users"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                )}
              </div>
              {onCollectionSelect && (
                <button
                  onClick={() => onCollectionSelect(collection)}
                  className="w-full px-3 py-1.5 text-sm text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  View Details
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {filteredCollections.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FolderPlus className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>No collections found</p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-primary-600 hover:text-primary-700"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <Modal
            title="Create Collection"
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreate}
          >
            <CollectionForm formData={formData} setFormData={setFormData} />
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && editingCollection && (
          <Modal
            title="Edit Collection"
            onClose={() => {
              setShowEditModal(false);
              setEditingCollection(null);
            }}
            onSave={handleEdit}
          >
            <CollectionForm formData={formData} setFormData={setFormData} />
          </Modal>
        )}
      </AnimatePresence>

      {/* Add Content Modal */}
      <AnimatePresence>
        {showAddContentModal && selectedCollection && (
          <AddContentModal
            collection={selectedCollection}
            tutorials={filteredTutorials}
            categories={filteredCategories}
            selectedTutorials={selectedTutorials}
            selectedCategories={selectedCategories}
            onToggleTutorial={(id) => {
              setSelectedTutorials(prev =>
                prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
              );
            }}
            onToggleCategory={(id) => {
              setSelectedCategories(prev =>
                prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
              );
            }}
            searchQuery={contentSearchQuery}
            onSearchChange={setContentSearchQuery}
            onClose={() => {
              setShowAddContentModal(false);
              setSelectedCollection(null);
            }}
            onSave={handleAddContent}
            onRemoveTutorial={handleRemoveTutorial}
            onRemoveCategory={handleRemoveCategory}
          />
        )}
      </AnimatePresence>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && selectedCollection && (
          <AssignModal
            collection={selectedCollection}
            users={filteredUsers}
            selectedUsers={selectedUsers}
            onToggleUser={(id) => {
              setSelectedUsers(prev =>
                prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]
              );
            }}
            searchQuery={userSearchQuery}
            onSearchChange={setUserSearchQuery}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedCollection(null);
            }}
            onSave={handleAssign}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Modal Component
interface ModalProps {
  title: string;
  onClose: () => void;
  onSave: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, onSave, children }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
        <div className="flex items-center justify-end space-x-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Save</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Collection Form Component
interface CollectionFormProps {
  formData: { name: string; description: string; icon: string };
  setFormData: (data: { name: string; description: string; icon: string }) => void;
}

const CollectionForm: React.FC<CollectionFormProps> = ({ formData, setFormData }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Collection name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          rows={3}
          placeholder="Collection description"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Emoji)</label>
        <input
          type="text"
          value={formData.icon}
          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="📚"
          maxLength={2}
        />
      </div>
    </div>
  );
};

// Add Content Modal Component
interface AddContentModalProps {
  collection: LearningCollection;
  tutorials: Tutorial[];
  categories: Category[];
  selectedTutorials: string[];
  selectedCategories: string[];
  onToggleTutorial: (id: string) => void;
  onToggleCategory: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClose: () => void;
  onSave: () => void;
  onRemoveTutorial: (collectionId: string, tutorialId: string) => void;
  onRemoveCategory: (collectionId: string, categoryId: string) => void;
}

const AddContentModal: React.FC<AddContentModalProps> = ({
  collection,
  tutorials,
  categories,
  selectedTutorials,
  selectedCategories,
  onToggleTutorial,
  onToggleCategory,
  searchQuery,
  onSearchChange,
  onClose,
  onSave,
  onRemoveTutorial,
  onRemoveCategory
}) => {
  const [activeTab, setActiveTab] = useState<'add' | 'manage'>('add');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-primary-50 to-primary-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Add Content to Collection</h3>
            <p className="text-sm text-gray-600 mt-1">{collection.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b bg-gray-50">
          <button
            onClick={() => setActiveTab('add')}
            className={cn(
              "px-6 py-3 font-semibold transition-colors relative",
              activeTab === 'add'
                ? "text-primary-600 bg-white"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <span className="relative z-10">➕ Add Content</span>
            {activeTab === 'add' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={cn(
              "px-6 py-3 font-semibold transition-colors relative",
              activeTab === 'manage'
                ? "text-primary-600 bg-white"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <span className="relative z-10">📋 Manage Existing</span>
            {activeTab === 'manage' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
            )}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'add' ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Quick Tip:</strong> Select tutorials and/or categories below, then click "Add Selected" to add them to this collection.
                </p>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="🔍 Search tutorials and categories..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-lg flex items-center space-x-2">
                      <BookOpen className="w-5 h-5 text-primary-600" />
                      <span>Tutorials</span>
                    </h4>
                    {selectedTutorials.length > 0 && (
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                        {selectedTutorials.length} selected
                      </span>
                    )}
                  </div>
                  <div 
                    className="max-h-80 overflow-y-auto space-y-2"
                    onScroll={(e) => {
                      const target = e.target as HTMLElement;
                      const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                      // Load more when within 100px of bottom
                      if (scrollBottom < 100 && tutorialsHasMore && !tutorialsLoading) {
                        loadTutorialsPage(tutorialsPage + 1, searchQuery);
                      }
                    }}
                  >
                    {tutorials.length > 0 ? (
                      tutorials.map((tutorial) => {
                        // Filter out tutorials that are already in the collection
                        const isAlreadyInCollection = collection.tutorials?.some(t => t.id === tutorial.id);
                        if (isAlreadyInCollection) return null;
                        
                        return (
                        <label
                          key={tutorial.id}
                          className={cn(
                            "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors border",
                            selectedTutorials.includes(tutorial.id)
                              ? "bg-primary-50 border-primary-300"
                              : "hover:bg-gray-50 border-gray-200"
                          )}
                        >
                          <div className="relative flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={selectedTutorials.includes(tutorial.id)}
                              onChange={() => onToggleTutorial(tutorial.id)}
                              className="w-5 h-5 rounded border-2 border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2 cursor-pointer checked:bg-primary-600 checked:border-primary-600 checked:text-white appearance-none relative"
                              style={{
                                accentColor: selectedTutorials.includes(tutorial.id) ? '#5a6fd8' : undefined
                              }}
                            />
                            {selectedTutorials.includes(tutorial.id) && (
                              <Check className="absolute top-0.5 left-0.5 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">{tutorial.title}</div>
                            <div className="text-xs text-gray-500">{tutorial.category || 'Uncategorized'}</div>
                          </div>
                          {selectedTutorials.includes(tutorial.id) && (
                            <Check className="w-4 h-4 text-primary-600" />
                          )}
                        </label>
                        );
                      }).filter(Boolean)
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p>
                          {searchQuery.trim() 
                            ? `No tutorials found matching "${searchQuery}"`
                            : tutorials.length === 0
                            ? 'No tutorials available'
                            : 'All tutorials are already in this collection'}
                        </p>
                        {searchQuery && (
                          <button
                            onClick={() => onSearchChange('')}
                            className="text-xs mt-2 text-primary-600 hover:text-primary-700 underline"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-lg flex items-center space-x-2">
                      <FolderTree className="w-5 h-5 text-primary-600" />
                      <span>Categories</span>
                    </h4>
                    {selectedCategories.length > 0 && (
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                        {selectedCategories.length} selected
                      </span>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {categories.length > 0 ? (
                      <CategoryTree
                        categories={categories}
                        selectedCategories={selectedCategories}
                        onToggleCategory={onToggleCategory}
                        collection={collection}
                        level={0}
                      />
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <FolderTree className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p>
                          {searchQuery.trim() 
                            ? `No categories found matching "${searchQuery}"`
                            : categories.length === 0
                            ? 'No categories available'
                            : 'All categories are already in this collection'}
                        </p>
                        {searchQuery && (
                          <button
                            onClick={() => onSearchChange('')}
                            className="text-xs mt-2 text-primary-600 hover:text-primary-700 underline"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {collection.tutorials && collection.tutorials.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Tutorials in Collection ({collection.tutorials.length})</h4>
                  <div className="space-y-2">
                    {collection.tutorials.map((tutorial, index) => {
                      // Use id if available, otherwise use index as fallback
                      const tutorialId = tutorial?.id || tutorial?.tutorialId || `tutorial-${index}`;
                      const tutorialTitle = tutorial?.title || tutorial?.name || 'Untitled Tutorial';
                      
                      if (!tutorial) return null;
                      
                      return (
                        <div
                          key={tutorialId}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900">{tutorialTitle}</div>
                            <div className="text-xs text-gray-500">{tutorial.category || tutorial.categoryName || 'Uncategorized'}</div>
                          </div>
                          <button
                            onClick={() => {
                              if (tutorial.id && collection.id) {
                                onRemoveTutorial(collection.id, tutorial.id);
                              } else if (tutorial.tutorialId && collection.id) {
                                onRemoveTutorial(collection.id, tutorial.tutorialId);
                              } else {
                                console.error('Missing IDs:', { tutorial, collectionId: collection.id });
                                toast.error('Cannot remove: missing tutorial or collection ID');
                              }
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 ml-2"
                            title="Remove tutorial from collection"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {collection.categories && collection.categories.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Categories in Collection ({collection.categories.length})</h4>
                  <div className="space-y-2">
                    {collection.categories.map((category, index) => {
                      // Use id if available, otherwise use index as fallback
                      const categoryId = category?.id || category?.categoryId || `category-${index}`;
                      const categoryName = category?.name || 'Unnamed Category';
                      
                      if (!category) return null;
                      
                      return (
                        <div
                          key={categoryId}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900">{categoryName}</div>
                            {category.description && (
                              <div className="text-xs text-gray-500 line-clamp-1">{category.description}</div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              if (category.id && collection.id) {
                                onRemoveCategory(collection.id, category.id);
                              } else if (category.categoryId && collection.id) {
                                onRemoveCategory(collection.id, category.categoryId);
                              } else {
                                console.error('Missing IDs:', { category, collectionId: collection.id });
                                toast.error('Cannot remove: missing category or collection ID');
                              }
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 ml-2"
                            title="Remove category from collection"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(!collection.tutorials || collection.tutorials.length === 0) &&
               (!collection.categories || collection.categories.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  No content in this collection yet
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {activeTab === 'add' && (
              <span>
                {selectedTutorials.length + selectedCategories.length > 0 ? (
                  <strong className="text-primary-600">
                    {selectedTutorials.length} tutorial{selectedTutorials.length !== 1 ? 's' : ''} and {selectedCategories.length} categor{selectedCategories.length !== 1 ? 'ies' : 'y'} selected
                  </strong>
                ) : (
                  'Select tutorials or categories to add'
                )}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {activeTab === 'add' && (
              <button
                onClick={onSave}
                disabled={selectedTutorials.length === 0 && selectedCategories.length === 0}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-semibold shadow-lg"
              >
                <Plus className="w-4 h-4" />
                <span>Add Selected ({selectedTutorials.length + selectedCategories.length})</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Assign Modal Component
interface AssignModalProps {
  collection: LearningCollection;
  users: any[];
  selectedUsers: string[];
  onToggleUser: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClose: () => void;
  onSave: () => void;
}

const AssignModal: React.FC<AssignModalProps> = ({
  collection,
  users,
  selectedUsers,
  onToggleUser,
  searchQuery,
  onSearchChange,
  onClose,
  onSave
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold">Assign Collection: {collection.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-2">
            {users.map((user) => (
              <label
                key={user.id}
                className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded cursor-pointer border"
              >
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => onToggleUser(user.id)}
                    className="w-5 h-5 rounded border-2 border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2 cursor-pointer checked:bg-primary-600 checked:border-primary-600 checked:text-white appearance-none relative"
                    style={{
                      accentColor: selectedUsers.includes(user.id) ? '#5a6fd8' : undefined
                    }}
                  />
                  {selectedUsers.includes(user.id) && (
                    <Check className="absolute top-0.5 left-0.5 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{user.name}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
              </label>
            ))}
            {users.length === 0 && (
              <div className="text-center text-gray-500 py-8">No users found</div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={selectedUsers.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Assign to {selectedUsers.length} User(s)</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Category Tree Component for nested categories
interface CategoryTreeProps {
  categories: Category[];
  selectedCategories: string[];
  onToggleCategory: (id: string) => void;
  collection: LearningCollection;
  level: number;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
  categories,
  selectedCategories,
  onToggleCategory,
  collection,
  level
}) => {
  // Filter out categories that are already in the collection first
  const availableCategories = categories.filter(category => {
    return category && category.id && !collection.categories?.some(c => c.id === category.id);
  });

  if (availableCategories.length === 0) {
    return null;
  }

  return (
    <>
      {availableCategories.map((category) => (
          <div key={category.id}>
            <label
              className={cn(
                "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors border",
                selectedCategories.includes(category.id)
                  ? "bg-primary-50 border-primary-300"
                  : "hover:bg-gray-50 border-gray-200"
              )}
              style={{ paddingLeft: `${12 + level * 20}px` }}
            >
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => onToggleCategory(category.id)}
                  className="w-5 h-5 rounded border-2 border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2 cursor-pointer checked:bg-primary-600 checked:border-primary-600 checked:text-white appearance-none relative"
                  style={{
                    accentColor: selectedCategories.includes(category.id) ? '#5a6fd8' : undefined
                  }}
                />
                {selectedCategories.includes(category.id) && (
                  <Check className="absolute top-0.5 left-0.5 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate flex items-center space-x-2">
                  {level > 0 && <span className="text-gray-400">└</span>}
                  <span>{category.name}</span>
                  {category.subcategories && category.subcategories.length > 0 && (
                    <span className="text-xs text-gray-400">
                      ({category.subcategories.length} subcategor{category.subcategories.length !== 1 ? 'ies' : 'y'})
                    </span>
                  )}
                </div>
                {category.description && (
                  <div className="text-xs text-gray-500 line-clamp-1">{category.description}</div>
                )}
              </div>
              {selectedCategories.includes(category.id) && (
                <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
              )}
            </label>
            {category.subcategories && category.subcategories.length > 0 && (
              <CategoryTree
                key={`tree-${category.id}`}
                categories={category.subcategories}
                selectedCategories={selectedCategories}
                onToggleCategory={onToggleCategory}
                collection={collection}
                level={level + 1}
              />
            )}
          </div>
        ))}
    </>
  );
};

export default CollectionManager;

